import React from 'react';
import { Link } from 'react-router-dom';
import HtmlContent from '../../../components/common/HtmlContent';
import useHashScroll from '../../../hooks/useHashScroll';

/**
 * InvestigatePage Component
 * 
 * Displays investigate tools content like the legacy HTML site
 */
const InvestigatePage = () => {
  // Use the hash scroll hook to scroll to the correct section
  useHashScroll();
  
  // Investigate intro content from the legacy HTML
  const investigateIntroHtml = `
    <p>
    After identifying and framing the problem, a more focused research phase is initiated. This comes after the problem phase as it's rare to start with a blank slate and go looking for a problem through unguided research. 
    </p>

    <p>
    A problem helps focus the research, which can then interrogate the problem and validate that the problem exists.
    </p>

    <p>
    There are a number of tools that can help with this validation and challenging the assumptions, and there is also a lot of cross over with design thinking - but like all tools, they are used depending on the scenario.
    </p>
  `;

  // Helper function to render tool info table with optional creator row
  const renderToolInfoTable = (category, updated, templates, creator = null) => {
    return (
      <div className="tool-table-wrap">
        <table id="tool-table">
          <tbody>
            <tr>
              <th colSpan="3">Tool Info</th>
            </tr>
            <tr>
              <td>Category</td>
              <td>{category}</td>  
            </tr>
            <tr>
              <td>Updated</td>
              <td>{updated}</td>
            </tr>
            <tr>
              <td>Templates</td>
              <td>{templates}</td>
            </tr>
            {creator && (
              <tr>
                <td>Creator</td>
                <td>{creator}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  // Templates HTML for each tool
  const researchCanvasTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/research/empathise/investigate/research-canvas.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/o9J_kois3So=/" className="link-table">Miro</a>
    </>
  );

  const competitorAnalysisTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/research/empathise/investigate/competitor-analysis.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/o9J_koh1sjE=/" className="link-table">Miro</a>
    </>
  );

  const stakeholderInterviewTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/research/empathise/investigate/stakeholder-interview.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/o9J_konMyto=/" className="link-table">Miro</a>
    </>
  );

  const userResearchCanvasTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/research/empathise/user-interview/user-research-canvas.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/o9J_koizhfE=/" className="link-table">Miro</a>
    </>
  );

  // Creator HTML
  const naCreator = <a href="">N/A</a>;
  const bernardRothCreator = <a href="https://dschool.stanford.edu/news-events/bernie-roth-the-achievement-habit">Bernard Roth</a>;
  const stephanieTroethCreator = <a href="https://twitter.com/sniffles">Stephanie Troeth</a>;
  const robWhitingCreator = <a href="https://twitter.com/whitingx">Rob Whiting</a>;

  return (
    <>
      {/* Investigate Intro */}
      <div className="tool-overview-container">
        <div className="tool-breadcrumb-container">
          <div className="tool-breadcrumb-wrap">
            <div className="tool-breadcrumb-icon">
              <i className="fas fa-bullhorn fa-2x"></i>
            </div>
            <div className="tool-breadcrumb-nav">
              <Link to="/research">Research Tools</Link>
              &nbsp;&nbsp;
              <i className="fas fa-angle-double-right"></i>
              &nbsp;&nbsp;
              <Link to="/research/empathise">Empathise</Link>
            </div>
            <div className="tool-breadcrumb-title">
              <Link to="/research/empathise/investigate">Investigate</Link>
            </div>
          </div>
        </div>
        <div className="tool-overview-para">
          <HtmlContent html={investigateIntroHtml} containerClass="" />
        </div>
        <div className="tool-section-container">
          <div className="tool-section-wrap">
            <div className="tool-section-title">Section Tools</div>
          </div>
          <div className="tool-section-list">
            <div><Link className="tool-section-cta" to="#research-canvas">Research Canvas</Link></div>
            <div><Link className="tool-section-cta" to="#competitor-analysis">Competitor Analysis</Link></div>
            <div><Link className="tool-section-cta" to="#stakeholder-interview">Stakeholder Interview</Link></div>
            <div><Link className="tool-section-cta" to="#user-research-canvas">User Research Canvas</Link></div>
          </div>
        </div>
      </div>

      {/* Research Canvas Section */}
      <main className="case-study">
        <span className="case-tool-title" id="research-canvas">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="research-canvas">
            <span className="tool-title-number">
              <Link to="/research/empathise/investigate#research-canvas">Research Canvas</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/empathise">Empathise</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/research/empathise/investigate">Investigate</Link>
          </div>
          <div className="tool-title-link">
            <Link to="/research/empathise/investigate#research-canvas"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-research-canvas" 
            id="tab-research-canvas-overview" 
            defaultChecked
          />
          <label htmlFor="tab-research-canvas-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("Investigate", "20/08/20", researchCanvasTemplates, stephanieTroethCreator)}
              <div className="tool-opening">
                <p>
                A good starting point to kick off the research phase is with a Research Canvas, this will help to clarify what you're hoping to get out of the research phased, what you're researching, what you want to test and what methodologies you will use, along with other things.
                </p>
                
                <p>
                This is a particularly helpful way of summarising things from the Problem Discovery phase, and sets things up for a higher chance of success in the Research phase.
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.nngroup.com/articles/which-ux-research-methods/" className="link-table">When to Use Which User-Experience Research Methods</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://uxdict.io/design-thinking-mixed-method-research-76d85ac5e759" className="link-table">Design Thinking: Mixed Method Research</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-research-canvas" 
            id="tab-research-canvas-template"
          />
          <label htmlFor="tab-research-canvas-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_kois3So=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Research Canvas Template"
            ></iframe>
          </div>
        </div>
      </main>

      {/* Competitor Analysis Section */}
      <main className="case-study">
        <span className="case-tool-title" id="competitor-analysis">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="competitor-analysis">
            <span className="tool-title-number">
              <Link to="/research/empathise/investigate#competitor-analysis">Competitor Analysis</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/empathise">Empathise</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/research/empathise/investigate">Investigate</Link>
          </div>
          <div className="tool-title-link">
            <Link to="/research/empathise/investigate#competitor-analysis"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-competitor-analysis" 
            id="tab-competitor-analysis-overview" 
            defaultChecked
          />
          <label htmlFor="tab-competitor-analysis-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("Investigate", "10/08/20", competitorAnalysisTemplates, naCreator)}
              <div className="tool-opening">
                <p>
                A great starting point. What else is out there? Has anyone attempted to solve this problem before? How have they done it? Is there a business model associated?
                </p>
                <p>
                Understanding what direct and non-direct competitors are doing can be extremely helpful, particular when it comes to sizing the opportunity and understanding the existing landscape. 
                </p>
                <p>
                Using a Competitor Analysis template, we can begin to paint a picture of how potential competitors are operating, including specific features and other work arounds that might be aiding them. 
                </p>
                <p>  
                We can also include information that could help other business areas like marketing. 
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://buffer.com/library/competitor-analysis/" className="link-table">How to Perform a Best-in-Class Competitor Analysis</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://usabilitygeek.com/how-to-do-ux-competitor-analysis/" className="link-table">How To Do A UX Competitor Analysis: A Step By Step Guide</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-competitor-analysis" 
            id="tab-competitor-analysis-template"
          />
          <label htmlFor="tab-competitor-analysis-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_koh1sjE=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Competitor Analysis Template"
            ></iframe>
          </div>
        </div>
      </main>

      {/* Stakeholder Interview Section */}
      <main className="case-study">
        <span className="case-tool-title" id="stakeholder-interview">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="stakeholder-interview">
            <span className="tool-title-number">
              <Link to="/research/empathise/investigate#stakeholder-interview">Stakeholder Interview</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/empathise">Empathise</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/research/empathise/investigate">Investigate</Link>
          </div>
          <div className="tool-title-link">
            <Link to="/research/empathise/investigate#stakeholder-interview"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-stakeholder-interview" 
            id="tab-stakeholder-interview-overview" 
            defaultChecked
          />
          <label htmlFor="tab-stakeholder-interview-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("Investigate", "10/08/20", stakeholderInterviewTemplates, naCreator)}
              <div className="tool-opening">
                <p>
                Any established company will have a rich resource of employees and other stakeholders (partners, core customers etc) that live and breath the product, the market and the industry. It is well worth the time to regularly have catch ups with relevant stakeholders and run developments past people. 
                </p>
                <p>
                Like user interviews, it is important to have a focus in these discussions and be conscious of people's time, but more they will also allow for discussion of other problems and also a means of capturing ideas. 
                </p>
                <p>    
                The challenge is keeping this focus and taking value from the time - the attached template will help with framing these sessions.
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://uxdesign.cc/ux-process-starts-at-stakeholder-interviews-not-user-interviews-4b464368d7d3" className="link-table">UX Process starts at Stakeholder Interviews, not User Interviews</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.userinterviews.com/ux-research-field-guide-chapter/internal-stakeholder-interviews" className="link-table">Internal Stakeholder Interviews</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.testingtime.com/en/blog/stakeholder-interview-guide/" className="link-table">The ultimate guide to conducting a stakeholder interview</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.userfocus.co.uk/articles/conducting-an-effective-stakeholder-interview.html" className="link-table">Conducting an effective stakeholder interview</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.thomasessl.com/blog/2017/1/31/stakeholder-interviews-and-8-questions-that-yield-unique-insights" className="link-table">Stakeholder interviews and 8 questions that yield unique insights</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-stakeholder-interview" 
            id="tab-stakeholder-interview-template"
          />
          <label htmlFor="tab-stakeholder-interview-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_konMyto=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Stakeholder Interview Template"
            ></iframe>
          </div>

          <input 
            type="radio" 
            name="tabs-stakeholder-interview" 
            id="tab-stakeholder-interview-tips"
          />
          <label htmlFor="tab-stakeholder-interview-tips">Tips</label>
          <div className="tab tab-tools">
            <ul className="bullet-tk">
              <li>Each stakeholder is a part of the project, so building a connection with them is important.</li>
              <li>Take the interview as an opportunity to motivate yourself, and use it as a source of inspiration and innovation.</li>
              <li>Be clear who you need to talk to, and why you need to talk to them.</li>
              <li>Take time between interviews to digest and record the interview - recharge.</li> 
              <li>Use the organising email to set up the interview and ask some generic questions in advance to help clarify the purpose.</li> 
              <li>Stakeholder interviews aren't just for the start of the project, regular check ins and updates can continue to contribute towards the success of a project.</li>
              <li>It's easy to get too casual with the conversation and not get the required information, so remain professional and keep on topic.</li>
              <li>And of course, silence is OK. No leading questions - and keep them open ended!</li>
            </ul>
          </div>
        </div>
      </main>

      {/* User Research Canvas Section */}
      <main className="case-study">
        <span className="case-tool-title" id="user-research-canvas">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="user-research-canvas">
            <span className="tool-title-number">
              <Link to="/research/empathise/investigate#user-research-canvas">User Research Canvas</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/empathise">Empathise</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/research/empathise/investigate">Investigate</Link>
          </div>
          <div className="tool-title-link">
            <Link to="/research/empathise/investigate#user-research-canvas"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-user-research-canvas" 
            id="tab-user-research-canvas-overview" 
            defaultChecked
          />
          <label htmlFor="tab-user-research-canvas-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("User Interview", "10/08/20", userResearchCanvasTemplates, robWhitingCreator)}
              <div className="tool-opening">
                <p>
                Speaking to users regularly is the most important part of any Product Development process but can often be overlooked, meaning assumptions about what they want going untested. This can result in wasted time, money and resource, and ultimately a failed project, something that no one wants. 
                </p>
                <br />
            
                <h4>Why conduct User Research?</h4>
                
                <p>
                    <ul className="bullet-tk">
                      <li>Aligns Team</li>
                      <li>Clarifies the problem for the team to understand.</li>
                      <li>Tests and challenges assumptions.</li>
                      <li>Detaches subjective taste from designs.</li>
                      <li>Reduces waste of resource and time and avoids building things no one wants.</li>
                      <li>Builds empathy with the user, understanding what they want.</li> 
                      <li>Validate/Challenge ideas.</li>
                    </ul>
                </p>
                    
                <h4>Why not ask users what they want?</h4>
            
                <p>
                    <ul className="bullet-tk">
                      <li>People are aware of their problems, but not necessarily how to solve them.</li>
                      <li>Each person is unique in their problems, but can share commonality that a single solution can cover.</li>
                      <li>By interpreting their problems you can pull out insights that can align and form a vision of a solution to cover moreproblems.</li>
                      <li>Asking them what they do, instead of what they want helps pull out further insights.</li> 
                    </ul>
                </p>
            
                <h4>Challenges of User Research</h4>
                    
                <p>
                    <ul className="bullet-tk">
                      <li>Creating clearly defined research goals.</li>
                      <li>Reluctance of organisation to back research.</li>
                      <li>Time or money limitations.</li>
                      <li>Access to users.</li>
                      <li>Users don't always say what they need, do what they say they do, or do what they think they do.</li>
                    </ul>
                </p>
            
                <h4>User Research Canvas</h4>
                
                <p>
                To set ourselves for the User Interview stage we can create a User Research Canvas. This Canvas helps us to lay out a number of things we hope to cover during this process, ultimately helping us to validate our assumptions.
                </p>
                <p>    
                By clearly planning this phase we can keep everyone aligned through the Background, Goals of the interviews, the Methods and the Outputs, along with a number of other factors. 
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://vaultstrategy.com/blog/user-research-template/" className="link-table">User Research Canvas</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.smashingmagazine.com/2013/09/5-step-process-conducting-user-research/" className="link-table">A 5-Step Process For Conducting User Research</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.interaction-design.org/literature/topics/user-research" className="link-table">What is User Research?</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://uxplanet.org/ultimate-guide-to-user-research-bed4a57d260" className="link-table">The Essential Guide to User Research</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-user-research-canvas" 
            id="tab-user-research-canvas-template"
          />
          <label htmlFor="tab-user-research-canvas-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_koizhfE=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="User Research Canvas Template"
            ></iframe>
          </div>
        </div>
      </main>
    </>
  );
};

export default InvestigatePage; 